import { useCallback, useContext } from 'react';
import { Lead } from 'models/Leads';
import {
  endOfMonth,
  getDate,
  isFirstDayOfMonth,
  isSameDay,
  isSaturday,
  isSunday,
} from 'date-fns';
import { getDayCellCommonClasses } from 'pages/calendar/common/cells/DayCellCommon.styles';
import useFormattedPrice from 'pages/calendar/common/cells/useFormattedPrice';
import useCalendarMode from 'pages/stackedCalendar/useCalendarMode';
import { getLeadCheckInDate, getLeadCheckOutDate } from 'utils/lead/leadUtils';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/tooltip/Tooltip';
import { ExclamationIconStyled } from 'components/icons/ExclamationIconStyled';
import MoonIcon from '../../../components/icons/Moon.svg?react';
import { isCheckinVisible } from '../Calendar.utils';
import { CalendarDay } from '../body/CalendarBody.types';
import CalendarContext from '../../stackedCalendar/CalendarContext';
import useResponsiveStyleCalendar from '../useResponsiveStyleCalendar';
import useCalendarBodySelection from '../common/body/selection/useCalendarBodySelection';
import {
  DayCellDate,
  DayCellMinStay,
  DayCellMinStayAndUnbookableWrapper,
  DayCellPricing,
  DayCellUnbookable,
  DayCellWrapper,
} from './DayCell.style';
import LeadView from './LeadView';
import JobView from './JobView';
import SelectionView from './SelectionView';
import DayCellIcons from './DayCellIcons';
import useDayCellIconsVisibility from './useDayCellIconsVisibility';

interface Props {
  calendarDay: CalendarDay;
}

const DayCell = ({ calendarDay }: Props) => {
  const formattedPrice = useFormattedPrice(calendarDay.price);
  const calendarStyleConstants = useResponsiveStyleCalendar();
  const { t } = useTranslation();
  const {
    isTurnoverIconVisible,
    isCheckInBlockedIconVisible,
    isCheckoutBlockedIconVisible,
    isUnbookableNight,
  } = useDayCellIconsVisibility(calendarDay);

  const { selectedProperty } = useContext(CalendarContext);
  const { isPricingMode, isCombinedMode } = useCalendarMode();
  const { handleMouseEnter } = useCalendarBodySelection();

  const { date: dayDate } = calendarDay;

  const isSaturdayOrEndOfMonth: boolean =
    isSaturday(dayDate) || isSameDay(dayDate, endOfMonth(dayDate));

  const leadsWithCheckin = calendarDay.leads?.filter((lead) => {
    return isSameDay(getLeadCheckInDate(lead), dayDate);
  });

  const leadsWithCheckout = calendarDay.leads?.filter((lead) => {
    return isSameDay(getLeadCheckOutDate(lead), dayDate);
  });

  const hidePriceBcOfLead =
    (calendarDay.leads?.length && !leadsWithCheckout?.length) ||
    leadsWithCheckin?.length;

  const getLeadComponent = (lead: Lead) => {
    const onSunday = isSunday(dayDate);
    const isMonthStart = isFirstDayOfMonth(dayDate);
    const showCheckin = isCheckinVisible(dayDate, lead);

    if (onSunday || showCheckin || isMonthStart) {
      return <LeadView lead={lead} date={dayDate} />;
    }

    return <div style={{ display: 'none' }} />;
  };

  const onSelectionMouseEnter = useCallback(() => {
    if (selectedProperty) {
      handleMouseEnter({
        date: dayDate,
        propertyUid: selectedProperty,
      });
    }
  }, [dayDate, selectedProperty]);

  if (calendarDay.isLoading) {
    return (
      <DayCellWrapper
        className={getDayCellCommonClasses(dayDate)}
        $calendarStyleConstants={calendarStyleConstants}
        $borderRight={isSaturdayOrEndOfMonth}
      >
        <DayCellDate>{getDate(dayDate)}</DayCellDate>
        <DayCellMinStay $isLoading />
        <DayCellPricing $isLoading />
      </DayCellWrapper>
    );
  }

  const isModeAllowedToShowCellInfo = isPricingMode || isCombinedMode;
  const inRed =
    (!calendarDay.availableForCheckIn ||
      calendarDay.unavailable ||
      isTurnoverIconVisible ||
      isCheckInBlockedIconVisible ||
      isCheckoutBlockedIconVisible) &&
    !hidePriceBcOfLead;

  return (
    <DayCellWrapper
      $inRed={inRed}
      $calendarStyleConstants={calendarStyleConstants}
      $borderRight={isSaturdayOrEndOfMonth}
      className={`body-day-cell ${getDayCellCommonClasses(dayDate)}`}
      data-day-date={dayDate.getTime()}
      data-property-uid={selectedProperty}
      onMouseEnter={onSelectionMouseEnter}
    >
      {!!selectedProperty && (
        <SelectionView dayDate={dayDate} propertyUid={selectedProperty} />
      )}

      <DayCellDate>{getDate(dayDate)}</DayCellDate>
      {isModeAllowedToShowCellInfo && !hidePriceBcOfLead && (
        <div>
          <DayCellMinStayAndUnbookableWrapper>
            {isUnbookableNight && (
              <Tooltip
                id={`unbookable_tooltip_${dayDate.getTime()}`}
                placement="top"
                text={t('pageCalendar.tooltip.unbookable')}
              >
                <DayCellUnbookable>
                  <ExclamationIconStyled />
                </DayCellUnbookable>
              </Tooltip>
            )}
            {!!calendarDay.minimumStay && (
              <DayCellMinStay>
                <MoonIcon /> <span>{calendarDay.minimumStay}</span>
              </DayCellMinStay>
            )}
          </DayCellMinStayAndUnbookableWrapper>

          {formattedPrice && <DayCellPricing>{formattedPrice}</DayCellPricing>}

          <DayCellIcons calendarDay={calendarDay} />
        </div>
      )}
      <JobView jobs={calendarDay.jobs} />
      {calendarDay.leads?.map((lead) => getLeadComponent(lead))}
    </DayCellWrapper>
  );
};

export default DayCell;
